import { useState, useEffect, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import "../InvoicesManagement.scss";
import InformationBox from "../../components/InformationBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";

import AddDeductionsModal from "./AddDeductionsModal";
import PageWrapper from "../../components/PageWrapper";
import ErrorBox from "../../components/ErrorBox";
import CustomButton from "../../components/CustomButton";
import addIconLight from "../../assets/add_icon_light.png";
import axiosClient from "../../services/axiosClient";
import editIcon from "../../assets/edit_icon.png";
import UpdateDeductionsModal from "./UpdateDeductionsModal";

const DeductionsPage = () => {
  const { t } = useTranslation("general");
  const [isFetchingDeductions, setIsFetchingDeductions] = useState(false);
  const [fetchingDeductionsError, setFetchingDeductionsError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deductions, setDeductions] = useState([]);
  const [showDeductionsModal, setShowDeductionsModal] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [selectedDeduction, setSelectedDeduction] = useState(null);
  const [showDeductionsEditModal, setShowDeductionsEditModal] = useState(false);

  const tableTitles = [
    { value: "name", keepOnMobile: true },
    { value: "price", keepOnMobile: true },
    { value: "notes", keepOnMobile: true },
  ];

  const tableRows = deductions.map((deduction) => [
    {
      value: deduction.name,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: deduction.price,
      type: TABLE_CELL_TYPES.MONEY,
      keepOnMobile: true,
      currency: "IQD",
    },
    {
      value: deduction.notes || t("noNotes"),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          title={t("edit")}
          icon={editIcon}
          iconSize={20}
          onClick={() => {
            setSelectedDeduction(deduction);
            setShowDeductionsEditModal(true);
          }}
          variant={"blank"}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  const getAllDeductionsData = async (page, searchQuery) => {
    try {
      setIsFetchingDeductions(true);
      const response = await axiosClient.get("/deductions/getDeductions", {
        params: {
          page,
          limit: 10,
          searchQuery,
        },
      });

      const deductions = response.data.deductions;

      setDeductions(deductions);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response?.data?.totalPages,
        totalRecords: response?.data?.totalDeductions,
      });
    } catch (error) {
      console.error(
        "Error fetching deductions:",
        error.response?.data || error
      );
      setFetchingDeductionsError("errorFetchingDeductions");
    } finally {
      setIsFetchingDeductions(false);
    }
  };

  // Effects
  useEffect(() => {
    getAllDeductionsData(pagination.currentPage, searchQuery);
  }, [pagination.currentPage]);

  return (
    <PageWrapper title={t("deductions")} withBackButton>
      <div className="u-margin--top_700" />

      {fetchingDeductionsError && (
        <ErrorBox>{t(fetchingDeductionsError)}</ErrorBox>
      )}
      {isFetchingDeductions && <Loader size={LOADER_SIZES.MEDIUM} />}

      <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end u-margin--bottom_400">
        <CustomButton
          title={t("addDeduction")}
          onClick={() => setShowDeductionsModal(true)}
          icon={addIconLight}
          iconSize={20}
        />
      </div>

      {!fetchingDeductionsError &&
        !isFetchingDeductions &&
        deductions.length === 0 && (
          <>
            <InformationBox>{t("noDeductionsFound")}</InformationBox>
          </>
        )}

      {!fetchingDeductionsError &&
        !isFetchingDeductions &&
        deductions.length > 0 && (
          <>
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
            />
          </>
        )}

      {showDeductionsModal && (
        <AddDeductionsModal
          onClose={() => setShowDeductionsModal(false)}
          onSuccess={() => {
            setShowDeductionsModal(false);
            getAllDeductionsData();
          }}
        />
      )}

      {showDeductionsEditModal && (
        <UpdateDeductionsModal
          deduction={selectedDeduction}
          onClose={() => setShowDeductionsEditModal(false)}
          onSuccess={() => {
            setShowDeductionsEditModal(false);
            getAllDeductionsData();
          }}
        />
      )}
    </PageWrapper>
  );
};

export default DeductionsPage;
